import axios from '../lib/axios';
import { showToast } from '../utils/toast';

// Updated Partner interface based on actual API response
export interface Partner {
  _id: string;
  name: string;
  email: string;
  mobileNo: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  groupId?: string;
  token?: string;
  isEv?: boolean;
  category?: string[];
  subCategory?: string[];
  dashboard?: boolean; // New field for dashboard access
  suspended: boolean;
  lender?: string;
  kycGstNo?: string;
  kycBank?: {
    accountNo?: string;
    holderName?: string;
    ifsc?: string;
  };
  kycShop?: {
    name?: string;
    address?: string;
    state?: string;
    city?: string;
    pin?: string;
    note?: string;
  };
  kycDocs?: {
    shopFront?: string;
    shopInner?: string;
    evCer?: string;
  };
  kyc?: {
    status?: boolean;
    bank?: boolean;
    shop?: boolean;
    docs?: boolean;
  };
  serialNumber?: number;
  zypeVersion?: string;
}

// API response format
interface PartnerApiResponse {
  status: string;
  result: {
    data: {
      totalMatch: number;
      pageNo: number;
      pageSize: number;
      results: Partner[];
    };
  };
}

/**
 * Fetch partners with pagination, sorting and filtering
 * @param token Authentication token
 * @param page Current page number
 * @param pageSize Number of items per page
 * @param searchQuery Search query for filtering
 * @param sortBy Field to sort by
 * @param sortOrder Sort direction (asc or desc)
 * @returns Promise with partners data and pagination info
 */
export const fetchPartners = async ({
  page = 1,
  pageSize = 10,
  sortField = "createdAt",
  sortOrder = "desc",
  search = "",
  token = "",
}: {
  page?: number;
  pageSize?: number;
  sortField?: string;
  sortOrder?: string;
  search?: string;
  token?: string;
}): Promise<{
  partners: Partner[];
  totalMatch: number;
  pageNo: number;
  pageSize: number;
  clientPaginated?: boolean;
  paginationMessage?: string;
}> => {
  try {
    // Get token from localStorage if not provided
    let authToken = token;
    if (!authToken) {
      authToken = localStorage.getItem('token') || '';
    }

    // Map client-side sort fields to API sort fields
    const fieldMap: Record<string, string> = {
      name: "name",
      email: "email",
      contactNo: "mobileNo",
      mobileNo: "mobileNo",
      groupId: "groupId",
      status: "status",
      createdAt: "createdAt",
    };

    const apiSortField = fieldMap[sortField] || "createdAt";

    // Properly format query params
    const params = new URLSearchParams();
    params.append("page", String(page));
    params.append("pageSize", String(pageSize));
    params.append("sortBy", apiSortField);
    params.append("sortOrder", sortOrder);
    if (search) params.append("searchQuery", search);

    const url = `/adminusers/v1/partner?${params.toString()}`;

    // Check if token exists
    if (!authToken) {
      throw new Error("No authentication token available");
    }

    const response = await axios.get<PartnerApiResponse>(url, {
      headers: {
        'Content-Type': 'application/json',
        'token': authToken
      }
    });

    if (!response.data || typeof response.data !== 'object') {
      throw new Error("Invalid API response format");
    }

    let partners: Partner[] = [];
    let totalMatch = 0;
    let pageNo = page;
    let actualPageSize = pageSize;
    let usingClientPagination = false;
    let paginationMessage = "Server-side pagination is active";

    // Check if the response follows the expected format
    if (response.data.status === "Request successful!" && response.data.result?.data?.results) {
      partners = response.data.result.data.results;
      totalMatch = response.data.result.data.totalMatch || partners.length;

      // Get actual page and pageSize from response if available
      if (response.data.result?.data?.pageNo !== undefined) {
        pageNo = response.data.result.data.pageNo;
      }

      if (response.data.result?.data?.pageSize !== undefined) {
        actualPageSize = response.data.result.data.pageSize;
      }

      // We trust the server to handle pagination correctly now that we have totalMatch
      // No need to implement client-side pagination as a fallback
      console.log(`Server pagination: Page ${pageNo} of ${Math.ceil(totalMatch / actualPageSize)}, showing ${partners.length} of ${totalMatch} total partners`);

      // Add serial numbers to partners based on their position in the paginated results
      partners = partners.map((partner, index) => {
        const serialNumber = (pageNo - 1) * actualPageSize + index + 1;
        return {
          ...partner,
          serialNumber: serialNumber
        };
      });
    } else {
      throw new Error("Unexpected API response structure");
    }

    return {
      partners,
      totalMatch,
      pageNo,
      pageSize: actualPageSize,
      clientPaginated: usingClientPagination,
      paginationMessage
    };
  } catch (error: any) {
    console.error("Error fetching partners:", error.message);
    throw error;
  }
};

/**
 * Fetch a single partner by ID
 * @param id Partner ID
 * @param token Authentication token
 * @returns Promise with partner data
 */
export const fetchPartnerById = async (
  id: string,
  token: string | null
): Promise<Partner> => {
  try {
    console.log(`[API] Fetching partner with ID: ${id}`);

    // Get token from localStorage if not provided
    let authToken = token;
    if (!authToken) {
      authToken = localStorage.getItem('token') || '';
      console.log("[API] Token from props was empty, using token from localStorage:", authToken ? "Found token" : "No token in localStorage");
    }

    const response = await axios.get(`/adminusers/v1/partner/${id}`, {
      headers: {
        'Content-Type': 'application/json',
        'token': authToken
      }
    });

    console.log('[API] Partner detail response:', response.data);

    if (response.data?.status === "Request successful!" && response.data.result?.data?.results?.[0]) {
      return response.data.result.data.results[0];
    }

    throw new Error('Invalid response format from API');
  } catch (error: any) {
    console.error('[API] Error fetching partner:');
    const errorMessage = error.response?.data?.message || 'Failed to fetch partner details';
    showToast.error(errorMessage);

    throw error;
  }
};

export const fetchPartnerByName = async (
  name: string,
  token: string | null
): Promise<Partner> => {
  try {
    console.log(`[API] Fetching partner with name: ${name}`);

    // ✅ Get token from localStorage if not provided
    let authToken = token;
    if (!authToken) {
      authToken = localStorage.getItem("localToken") || "";
      console.log(
        "[API] Token from props was empty, using token from localStorage:",
        authToken ? "Found token" : "No token in localStorage"
      );
    }

    // ✅ Correct API endpoint
    const response = await axios.get(
      `/adminusers/v1/partner?name=${encodeURIComponent(name)}`,
      {
        headers: {
          "Content-Type": "application/json",
          token: authToken,
        },
      }
    );

    console.log("[API] Partner detail response:", response.data);

    // ✅ FIX: your API returns single 'data', not 'results'
    const partner = response.data?.result?.data;

    if (response.data?.status === "Request successful!" && partner) {
      return partner;
    }

    throw new Error("Invalid response format from API");
  } catch (error: any) {
    console.error("[API] Error fetching partner:", error);
    const errorMessage =
      error.response?.data?.message ||
      "Failed to fetch partner details";
    showToast.error(errorMessage);
    throw error;
  }
};



// Update interfaces for create and update
type CreatePartnerData = {
  name: string;
  groupId: string;
  mobileNo: string;
  email: string;
  isEv?: boolean;
  category?: string[];
  subCategory?: string[];
  dashboard?: boolean; // New field for dashboard access
  suspended: boolean; // Ensure this is always set
  lender?: string;
  kycGstNo?: string;
  kyc?: {
    bank?: {
      accountNo?: string;
      holderName?: string;
      ifsc?: string;
    };
    shop?: {
      name?: string;
      address?: string;
      state?: string;
      city?: string;
      pin?: string;
    };
    docs?: {
      shopFront?: File | null;
      shopInner?: File | null;
      evCer?: File | null;
    };
  };
};

/**
 * Create a new partner
 * @param partnerData Partner data to create
 * @param token Authentication token
 * @param docFiles Optional document files for KYC
 * @returns Promise with the created partner
 */
export const createPartner = async (
  partnerData: CreatePartnerData,
  token: string | null,
  docFiles?: {
    shopFront?: File | null;
    shopInner?: File | null;
    evCer?: File | null;
  }
): Promise<Partner> => {
  try {
    // Get token from localStorage if not provided
    let authToken = token;
    if (!authToken) {
      authToken = localStorage.getItem('token') || '';
    }

    // Check if token exists
    if (!authToken) {
      throw new Error("No authentication token available");
    }

    console.log("[API Debug] Creating partner with data:", JSON.stringify(partnerData, null, 2));
    console.log("[API Debug] Token available:", !!authToken);

    // Convert form data to the structure expected by the API
    const apiPartnerData: any = {
      name: partnerData.name,
      groupId: partnerData.groupId || partnerData.name, // Ensure groupId is present
      mobileNo: partnerData.mobileNo,
      email: partnerData.email,
      isEv: partnerData.isEv || false,
      category: partnerData.category || [],
      subCategory: partnerData.subCategory || [],
      dashboard: partnerData.dashboard || false, // New field for dashboard access
      suspended: partnerData.suspended || false, // Ensure suspended is always set
    };

    // Add lender if provided
    if (partnerData.lender) {
      apiPartnerData.lender = partnerData.lender;
    }

    if (partnerData.kycGstNo) {
      apiPartnerData.kycGstNo = partnerData.kycGstNo;
    }

    // Initialize KYC flags
    const kycFlags = {
      bank: false,
      shop: false,
      docs: false,
      status: 'false' as 'true' | 'false'
    };

    // Handle kycBank if present
    if (partnerData.kyc?.bank) {
      apiPartnerData.kycBank = {
        accountNo: partnerData.kyc.bank.accountNo,
        holderName: partnerData.kyc.bank.holderName,
        ifsc: partnerData.kyc.bank.ifsc
      };
      kycFlags.bank = true;
    }

    // Handle kycShop if present
    if (partnerData.kyc?.shop) {
      apiPartnerData.kycShop = {
        name: partnerData.kyc.shop.name,
        address: partnerData.kyc.shop.address,
        state: partnerData.kyc.shop.state,
        city: partnerData.kyc.shop.city,
        pin: partnerData.kyc.shop.pin
      };
      kycFlags.shop = true;
    }

    // Handle documents if present
    if (docFiles && (docFiles.shopFront || docFiles.shopInner || docFiles.evCer)) {
      kycFlags.docs = true;
    }

    // NOTE: No longer automatically setting KYC status
    // KYC status will need to be set manually via the admin interface
    kycFlags.status = 'false';

    // Add KYC flags to the API data
    apiPartnerData.kyc = kycFlags;

    console.log("[API Debug] Transformed API data:", JSON.stringify(apiPartnerData, null, 2));
    console.log("[API Debug] Document files present:", !!docFiles);

    // Step 1: Create the partner
    try {
      const response = await axios.post('/adminusers/v1/partner', apiPartnerData, {
        headers: {
          'Content-Type': 'application/json',
          'token': authToken
        }
      });

      console.log("[API Debug] Partner creation response:", response.data);

      let createdPartner: Partner;

      // Handle successful response with different formats
      if (response.data?.status === "Request successful!" && response.data.result?.data?.results?.[0]) {
        // Standard partner response format
        createdPartner = response.data.result.data.results[0];
      } else if (response.data?.status === "Created!" && response.data.result?.user) {
        // Alternative format when creating a partner
        // Map the user object to our Partner interface
        createdPartner = {
          _id: response.data.result.user._id,
          name: response.data.result.user.name,
          email: response.data.result.user.email,
          mobileNo: response.data.result.user.mobileNo,
          status: response.data.result.user.suspended ? 'suspended' : 'active',
          createdAt: response.data.result.user.createdAt,
          updatedAt: response.data.result.user.updatedAt,
          groupId: response.data.result.user.groupId,
          token: response.data.result.user.token,
          isEv: response.data.result.user.isEv || false,
          category: response.data.result.user.category || [],
          subCategory: response.data.result.user.subCategory || [],
          dashboard: response.data.result.user.dashboard || false, // New field for dashboard access
          suspended: response.data.result.user.suspended || false,
          lender: response.data.result.user.lender || '',
          kycGstNo: response.data.result.user.kycGstNo,
          kycBank: response.data.result.user.kycBank,
          kycShop: response.data.result.user.kycShop,
          kycDocs: response.data.result.user.kycDocs,
          kyc: response.data.result.user.kyc || {
            status: '',
            bank: false,
            shop: false,
            docs: false
          }
        };
      } else {
        throw new Error('Invalid response format from API');
      }

      // Step 2: Upload documents if provided
      const mobileNo = createdPartner.mobileNo;

      if (docFiles && mobileNo) {
        const uploadPromises: Promise<any>[] = [];

        // Upload Shop Front image if exists
        if (docFiles.shopFront) {
          uploadPromises.push(uploadPartnerDocument('partner', 'shopFront', mobileNo, docFiles.shopFront, authToken));
        }

        // Upload Shop Inner image if exists
        if (docFiles.shopInner) {
          uploadPromises.push(uploadPartnerDocument('partner', 'shopInner', mobileNo, docFiles.shopInner, authToken));
        }

        // Upload EV Certificate if exists and partner is EV
        if (docFiles.evCer && partnerData.isEv) {
          uploadPromises.push(uploadPartnerDocument('partner', 'evCer', mobileNo, docFiles.evCer, authToken));
        }

        // Wait for all uploads to complete
        if (uploadPromises.length > 0) {
          try {
            console.log("[API Debug] Uploading", uploadPromises.length, "document files");
            await Promise.all(uploadPromises);
            console.log('[API] Successfully uploaded all partner documents');
          } catch (uploadError) {
            console.error('[API] Error uploading some partner documents:', uploadError);
            // We don't throw here as the partner is already created
          }
        }
      }

      return createdPartner;
    } catch (error: any) {
      console.error("[API Debug] Detailed error:", error.response?.status, error.response?.data);
      console.error("[API Debug] Request URL:", error.config?.url);
      console.error("[API Debug] Request Headers:", error.config?.headers);
      throw error;
    }
  } catch (error: any) {
    console.error('[API] Error creating partner:', error.message);
    const errorMessage = error.response?.data?.message || 'Failed to create partner';
    showToast.error(errorMessage);

    throw error;
  }
};

/**
 * Upload document for partner KYC
 * @param type User type (hardcoded as 'partner')
 * @param docType Document type (shopFront, shopInner, evCer)
 * @param mobileNo Mobile number of the partner
 * @param file File to upload
 * @param token Authentication token
 * @returns Promise indicating success
 */
export const uploadPartnerDocument = async (
  type: string,
  docType: 'shopFront' | 'shopInner' | 'evCer',
  mobileNo: string,
  file: File,
  token: string
): Promise<boolean> => {
  try {
    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      console.error(`[API] Invalid file type for ${docType}. Only PNG, JPG, and JPEG are allowed.`);
      showToast.error(`Invalid file type. Only PNG, JPG, and JPEG are allowed.`);
      return false;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      console.error(`[API] File too large for ${docType}. Maximum size is 5MB.`);
      showToast.error(`File too large. Maximum size is 5MB.`);
      return false;
    }

    // Create form data
    const formData = new FormData();
    formData.append('file', file);

    // Use 'partner' as the user type in the endpoint
    const endpoint = `/adminusers/partner/docs/${docType}/${mobileNo}`;

    console.log(`[API Debug] Document upload details:`);
    console.log(`[API Debug] - DocType: ${docType}`);
    console.log(`[API Debug] - Mobile: ${mobileNo}`);
    console.log(`[API Debug] - Endpoint: ${endpoint}`);
    console.log(`[API Debug] - File name: ${file.name}`);
    console.log(`[API Debug] - File size: ${(file.size / 1024 / 1024).toFixed(2)}MB`);

    try {
      // First attempt with multipart/form-data (standard for file uploads)
      console.log(`[API Debug] Attempting upload with multipart/form-data...`);
      const response = await axios.put(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'token': token
        }
      });

      console.log(`[API Debug] Upload success with status: ${response.status}`);
      return true;
    } catch (formDataError: any) {
      console.error(`[API Debug] Form data upload failed:`, formDataError.message);

      if (formDataError.response?.data?.status === 'Bad request, malformed JSON') {
        // The server might be expecting a different format
        console.log(`[API Debug] Server reported malformed JSON, trying alternative format...`);

        try {
          // Read the file as base64
          const base64Data = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = (error) => reject(error);
          });

          // Format: data:image/jpeg;base64,/9j/4AAQSkZJRgABAQEAYABgAAD/...
          // Extract only the base64 part after the comma
          const base64Content = base64Data.split(',')[1];

          // Create JSON payload
          const jsonPayload = {
            fileName: file.name,
            fileType: file.type,
            fileSize: file.size,
            fileContent: base64Content
          };

          // Try with JSON format
          console.log(`[API Debug] Trying JSON format with base64 encoded file...`);
          const jsonResponse = await axios.put(endpoint, jsonPayload, {
            headers: {
              'Content-Type': 'application/json',
              'token': token
            }
          });

          console.log(`[API Debug] JSON upload success with status: ${jsonResponse.status}`);
          return true;
        } catch (jsonError: any) {
          console.error(`[API Debug] JSON upload also failed:`, jsonError.message);

          try {
            // Try with URL encoded form
            const urlEncodedForm = new URLSearchParams();
            // We can't include the file directly in URL encoded form, but we can add metadata
            urlEncodedForm.append('filename', file.name);
            urlEncodedForm.append('filetype', file.type);
            urlEncodedForm.append('filesize', file.size.toString());

            console.log(`[API Debug] Trying with x-www-form-urlencoded (metadata only)...`);
            const urlEncodedResponse = await axios.put(endpoint, urlEncodedForm, {
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
                'token': token
              }
            });

            console.log(`[API Debug] URL encoded upload success with status: ${urlEncodedResponse.status}`);
            return true;
          } catch (urlEncodedError: any) {
            console.error(`[API Debug] URL encoded upload also failed:`, urlEncodedError.message);
            // If all attempts fail, throw the original error
            throw formDataError;
          }
        }
      } else {
        // If it's not a malformed JSON error, just throw the original error
        throw formDataError;
      }
    }
  } catch (error: any) {
    console.error(`[API] Error uploading ${docType} document:`, error.message);
    if (error.response) {
      console.error(`[API] Response status:`, error.response.status);
      console.error(`[API] Response data:`, error.response.data);
    }

    const errorMessage = error.response?.data?.message ||
      error.response?.data?.result ||
      `Failed to upload ${docType} document`;
    showToast.error(errorMessage);
    throw error;
  }
};

/**
 * Update an existing partner
 * @param idOrNameOrMobile Partner ID, name, or mobile number to update
 * @param partnerData Partner data to update
 * @param token Authentication token
 * @param docFiles Optional document files for KYC
 * @returns Promise with the updated partner
 */
export const updatePartner = async (
  idOrNameOrMobile: string,
  partnerData: Partial<{
    name: string;
    groupId: string;
    mobileNo: string;
    email: string;
    suspended: boolean;
    category: string[];
    subCategory: string[];
    dashboard?: boolean; // New field for dashboard access
    isEv: boolean;
    lender: string;
    kycGstNo?: string;
    kycBank?: {
      accountNo?: string;
      holderName?: string;
      ifsc?: string;
    };
    kycShop?: {
      name?: string;
      address?: string;
      state?: string;
      city?: string;
      pin?: string;
      note?: string;
    };
    kyc?: {
      status?: boolean;
      bank?: boolean;
      shop?: boolean;
      docs?: boolean;
    };
    activated?: boolean;
    verified?: boolean;
  }>,
  token: string | null,
  docFiles?: {
    shopFront?: File | null;
    shopInner?: File | null;
    evCer?: File | null;
  }
): Promise<Partner> => {
  try {
    // Get token from localStorage if not provided
    let authToken = token;
    if (!authToken) {
      authToken = localStorage.getItem('token') || '';
    }

    // Check if token exists
    if (!authToken) {
      throw new Error("No authentication token available");
    }

    // Only set KYC flags if KYC-specific data is provided and partnerData.kyc isn't already set
    if (!partnerData.kyc) {
      // Initialize KYC flags only if we're updating KYC-related fields
      if (partnerData.kycBank || partnerData.kycShop ||
        (docFiles && (docFiles.shopFront || docFiles.shopInner || docFiles.evCer))) {

        const kycFlags: {
          bank?: boolean;
          shop?: boolean;
          docs?: boolean;
          status?: boolean;
        } = {};

        // Update bank flag only if bank details are provided
        if (partnerData.kycBank) {
          kycFlags.bank = true;
        }

        // Update shop flag only if shop details are provided
        if (partnerData.kycShop) {
          kycFlags.shop = true;
        }

        // Update docs flag only if document files are provided
        if (docFiles && (docFiles.shopFront || docFiles.shopInner || docFiles.evCer)) {
          kycFlags.docs = true;
        }

        // NOTE: No longer automatically setting KYC status in update
        // Status will need to be set manually via the admin interface

        // Only add KYC object if we have flags to set
        if (Object.keys(kycFlags).length > 0) {
          partnerData.kyc = kycFlags;
        }
      }
    }

    console.log(`[Update Debug] Starting partner update with identifier: ${idOrNameOrMobile}`);
    console.log(`[Update Debug] Original input data:`, JSON.stringify(partnerData, null, 2));
    console.log(`[Update Debug] Auth token available:`, !!authToken);

    // Check for UI component input format with contactNo and contactEmail instead of mobileNo and email
    const adaptedData = adaptPartnerData(partnerData);

    // Validate the input data with simple checks
    if (adaptedData.mobileNo && !/^\d{10}$/.test(adaptedData.mobileNo)) {
      throw new Error("Mobile number must be 10 digits");
    }

    // Determine the type of identifier and build the appropriate endpoint
    let endpoint = '/adminusers/v1/partner';
    let queryParams = new URLSearchParams();

    // Based on the server implementation, we need to use query params for all types
    const identifierType = getIdentifierType(idOrNameOrMobile);
    console.log(`[Update] Identifier type detected: ${identifierType}`);

    if (identifierType === 'id') {
      queryParams.append('id', idOrNameOrMobile);
    } else if (identifierType === 'mobile') {
      queryParams.append('mobileNo', idOrNameOrMobile);
    } else if (identifierType === 'name') {
      queryParams.append('name', idOrNameOrMobile.trim());
    }

    // Add query params to endpoint
    endpoint += `?${queryParams.toString()}`;

    // Create a clean data object including new fields
    const updateData: Record<string, any> = {};

    // Add basic fields with actual values, all properly trimmed
    if (adaptedData.name) updateData.name = adaptedData.name.trim();
    if (adaptedData.email) updateData.email = adaptedData.email.trim();
    if (adaptedData.mobileNo) updateData.mobileNo = adaptedData.mobileNo.trim();
    if (adaptedData.groupId) {
      updateData.groupId = adaptedData.groupId.trim();
    } else if (adaptedData.name) {
      updateData.groupId = adaptedData.name.trim(); // Ensure groupId matches name if not provided
    }

    // Add new fields
    if (adaptedData.isEv !== undefined) updateData.isEv = adaptedData.isEv;
    if (adaptedData.lender !== undefined) updateData.lender = adaptedData.lender;
    if (adaptedData.category !== undefined) updateData.category = adaptedData.category;
    if (adaptedData.subCategory !== undefined) updateData.subCategory = adaptedData.subCategory;
    if (adaptedData.dashboard !== undefined) updateData.dashboard = adaptedData.dashboard;
    if (adaptedData.suspended !== undefined) updateData.suspended = adaptedData.suspended;
    if (adaptedData.kycGstNo !== undefined) updateData.kycGstNo = adaptedData.kycGstNo;
    if (adaptedData.activated !== undefined) updateData.activated = adaptedData.activated;
    if (adaptedData.verified !== undefined) updateData.verified = adaptedData.verified;

    // Add KYC data if present
    if (adaptedData.kyc) {
      updateData.kyc = adaptedData.kyc;
    }

    // Add direct kycBank field if present (matches backend structure)
    if (adaptedData.kycBank) {
      updateData.kycBank = adaptedData.kycBank;
    }

    // Add direct kycShop field if present (matches backend structure)
    if (adaptedData.kycShop) {
      // Convert pincode to pin if it exists - fixing the incorrect condition
      if (adaptedData.kycShop.pin && adaptedData.kycShop.pin) {
        adaptedData.kycShop.pin = adaptedData.kycShop.pin;
      }
      updateData.kycShop = adaptedData.kycShop;
    }

    console.log(`[Update Debug] Final endpoint: ${endpoint}`);
    console.log(`[Update Debug] Update request data:`, JSON.stringify(updateData, null, 2));

    // Check if we have actual data to update
    if (Object.keys(updateData).length === 0) {
      throw new Error("No valid data provided for update");
    }

    // Step 1: Update partner data
    let updatedPartner: Partner;

    try {
      // First attempt: Standard JSON method
      console.log('[Update Debug] Trying with JSON content type');
      console.log('[Update Debug] Final endpoint:', endpoint);
      console.log('[Update Debug] Update request data:', JSON.stringify(updateData, null, 2));

      const response = await axios.put(endpoint, updateData, {
        headers: {
          'Content-Type': 'application/json',
          'token': authToken
        }
      });

      console.log('[Update Debug] Response status:', response.status);
      console.log('[Update Debug] Response data:', JSON.stringify(response.data, null, 2));

      if (response.data?.status === "Request successful!" ||
        response.data?.status === "Updated!" ||
        response.status === 200) {

        // Map the response based on its structure
        if (response.data.result?.user) {
          updatedPartner = mapUserToPartner(response.data.result.user);
        } else if (response.data.result?.data?.results?.[0]) {
          updatedPartner = response.data.result.data.results[0];
        } else {
          throw new Error('Invalid response format from API');
        }
      } else {
        throw new Error('Invalid response format from API');
      }
    } catch (jsonError: any) {
      // Enhanced error logging
      console.error("[Update Debug] Detailed error:", jsonError.response?.status, jsonError.response?.data);
      console.error("[Update Debug] Request URL:", jsonError.config?.url);
      console.error("[Update Debug] Request Headers:", jsonError.config?.headers);

      // If JSON format failed with a 400 Bad Request and the error suggests malformed JSON,
      // try with a URL-encoded form approach as fallback
      if (jsonError.response?.status === 400 &&
        (jsonError.response?.data?.status === "Bad request, malformed JSON" ||
          jsonError.response?.data?.result === "none")) {

        console.log('[Update Debug] JSON approach failed with malformed JSON error, trying URL-encoded form data');

        // Convert data to URL-encoded form data format
        const formData = new URLSearchParams();
        Object.entries(updateData).forEach(([key, value]) => {
          // Handle nested objects
          if (typeof value === 'object' && value !== null) {
            formData.append(key, JSON.stringify(value));
          } else {
            formData.append(key, String(value));
          }
        });

        try {
          const formResponse = await axios.put(endpoint, formData.toString(), {
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
              'token': authToken
            }
          });

          console.log('[Update Debug] Form data response status:', formResponse.status);
          console.log('[Update Debug] Form data response:', JSON.stringify(formResponse.data, null, 2));

          if (formResponse.data?.status === "Request successful!" ||
            formResponse.data?.status === "Updated!" ||
            formResponse.status === 200) {

            // Map the response
            if (formResponse.data.result?.user) {
              updatedPartner = mapUserToPartner(formResponse.data.result.user);
            } else if (formResponse.data.result?.data?.results?.[0]) {
              updatedPartner = formResponse.data.result.data.results[0];
            } else {
              throw new Error('Invalid response format from API (form data approach)');
            }
          } else {
            throw new Error('Invalid response format from API (form data approach)');
          }
        } catch (formError: any) {
          console.error('[Update Debug] Form data approach also failed:', formError.message);
          throw formError; // Re-throw to be caught by outer catch
        }
      } else {
        // If error isn't related to malformed JSON, just throw it
        throw jsonError;
      }
    }

    // Step 2: Upload documents if provided
    if (docFiles && adaptedData.mobileNo) {
      const mobileNo = adaptedData.mobileNo;
      const uploadPromises: Promise<any>[] = [];

      // Upload Shop Front image if exists
      if (docFiles.shopFront) {
        uploadPromises.push(uploadPartnerDocument('partner', 'shopFront', mobileNo, docFiles.shopFront, authToken));
      }

      // Upload Shop Inner image if exists
      if (docFiles.shopInner) {
        uploadPromises.push(uploadPartnerDocument('partner', 'shopInner', mobileNo, docFiles.shopInner, authToken));
      }

      // Upload EV Certificate if exists and partner is EV
      if (docFiles.evCer && (adaptedData.isEv || updatedPartner.isEv)) {
        uploadPromises.push(uploadPartnerDocument('partner', 'evCer', mobileNo, docFiles.evCer, authToken));
      }

      // Wait for all uploads to complete
      if (uploadPromises.length > 0) {
        try {
          await Promise.all(uploadPromises);
          console.log('[API] Successfully uploaded all partner documents');
        } catch (uploadError) {
          console.error('[API] Error uploading some partner documents:', uploadError);
        }
      }
    }

    return updatedPartner!;
  } catch (error: any) {
    console.error('[API] Error updating partner:', error.message);

    // Enhanced error logging
    if (error.response) {
      console.error('[Update Debug] Response status:', error.response.status);
      console.error('[Update Debug] Response data:', JSON.stringify(error.response.data, null, 2));

      const serverMessage = error.response.data?.message ||
        error.response.data?.error ||
        (typeof error.response.data?.result === 'string' ? error.response.data.result : 'Unknown error');

      showToast.error(serverMessage || 'Failed to update partner');
    } else {
      showToast.error('Failed to update partner: Network error');
    }

    throw error;
  }
};

/**
 * Adapter function to handle different input formats for partner data
 * The UI component might use contactNo/contactEmail while the API expects mobileNo/email
 */
function adaptPartnerData(data: any): Partial<{
  name: string;
  groupId: string;
  mobileNo: string;
  email: string;
  suspended: boolean;
  category: string[];
  subCategory: string[];
  dashboard?: boolean; // New field for dashboard access
  isEv: boolean;
  lender: string;
  kycGstNo?: string;
  kycBank?: {
    accountNo?: string;
    holderName?: string;
    ifsc?: string;
  };
  kycShop?: {
    name?: string;
    address?: string;
    state?: string;
    city?: string;
    pin?: string;
    note?: string;
  };
  kyc?: {
    status?: boolean;
    bank?: boolean;
    shop?: boolean;
    docs?: boolean;
  };
  activated?: boolean;
  verified?: boolean;
}> {
  // Create a new object to avoid modifying the original
  const adaptedData: any = { ...data };

  // Handle contactNo (from UI) → mobileNo (for API)
  if (adaptedData.contactNo !== undefined && adaptedData.mobileNo === undefined) {
    adaptedData.mobileNo = adaptedData.contactNo;
    delete adaptedData.contactNo;
  }

  // Handle contactEmail (from UI) → email (for API)
  if (adaptedData.contactEmail !== undefined && adaptedData.email === undefined) {
    adaptedData.email = adaptedData.contactEmail;
    delete adaptedData.contactEmail;
  }

  // Ensure groupId matches name if not provided
  if (adaptedData.name && !adaptedData.groupId) {
    adaptedData.groupId = adaptedData.name;
  }

  return adaptedData;
}

// Helper function to determine identifier type
function getIdentifierType(identifier: string): 'id' | 'mobile' | 'name' {
  // If it looks like a MongoDB ID (24 hex characters)
  if (/^[0-9a-fA-F]{24}$/.test(identifier)) {
    return 'id';
  }

  // If it looks like a mobile number (10 digits)
  if (/^\d{10}$/.test(identifier)) {
    return 'mobile';
  }

  // Otherwise, assume it's a name
  return 'name';
}

// Helper function to handle successful response
function handleSuccessResponse(response: any): Partner {
  if (response.data?.status === "Request successful!" || response.data?.status === "Updated!") {
    // Map the response based on its structure
    if (response.data.result?.data?.results?.[0]) {
      return response.data.result.data.results[0];
    } else if (response.data.result?.user) {
      return mapUserToPartner(response.data.result.user);
    }
  }

  throw new Error('Invalid response format from API');
}

// Helper function to map user object to Partner interface
function mapUserToPartner(user: any): Partner {
  return {
    _id: user._id,
    name: user.name,
    email: user.email,
    mobileNo: user.mobileNo,
    status: user.suspended ? 'suspended' : 'active',
    suspended: user.suspended,
    createdAt: user.createdAt,
    updatedAt: user.updatedAt,
    groupId: user.groupId,
    token: user.token,
    isEv: user.isEv,
    lender: user.lender,
    kycGstNo: user.kycGstNo,
    kycBank: user.kycBank,
    kycShop: user.kycShop,
    kycDocs: user.kycDocs,
    kyc: user.kyc || {
      status: '',
      bank: false,
      shop: false,
      docs: false
    }
  };
}

/**
 * Delete a partner
 * @param nameOrMobile Partner name, or mobile number to delete
 * @param token Authentication token
 * @returns Promise indicating success
 */
export const deletePartner = async (
  nameOrMobile: string,
  token: string | null
): Promise<boolean> => {
  try {
    // Get token from localStorage if not provided
    let authToken = token;
    if (!authToken) {
      authToken = localStorage.getItem('token') || '';
    }

    // Check if token exists
    if (!authToken) {
      throw new Error("No authentication token available");
    }

    console.log(`[Delete] Starting partner deletion with identifier: ${nameOrMobile}`);

    // Try name first
    try {
      const nameEndpoint = `/adminusers/v1/partner?name=${encodeURIComponent(nameOrMobile)}`;
      console.log(`[Delete] Trying with name query param: ${nameEndpoint}`);

      const nameResponse = await axios.delete(nameEndpoint, {
        headers: {
          'Content-Type': 'application/json',
          'token': authToken
        }
      });

      return handleDeleteSuccess(nameResponse);
    } catch (nameError: any) {
      console.error('[Delete] Name approach failed:', nameError.message);
      // Continue to next method
    }

    // Try mobile number second
    try {
      const mobileEndpoint = `/adminusers/v1/partner?mobileNo=${nameOrMobile}`;
      console.log(`[Delete] Trying with mobileNo query param: ${mobileEndpoint}`);

      const mobileResponse = await axios.delete(mobileEndpoint, {
        headers: {
          'Content-Type': 'application/json',
          'token': authToken
        }
      });

      return handleDeleteSuccess(mobileResponse);
    } catch (mobileError: any) {
      console.error('[Delete] mobileNo approach failed:', mobileError.message);
      throw mobileError;
    }
  } catch (error: any) {
    console.error('[API] Error deleting partner:', error.message);

    // More specific error based on status code
    let errorMessage = 'Failed to delete partner';
    if (error.response) {
      if (error.response.status === 404) {
        errorMessage = 'Partner not found. Please check the name or mobile number.';
      } else if (error.response.status === 403) {
        errorMessage = 'You do not have permission to delete this partner.';
      } else if (error.response.data?.message) {
        errorMessage = error.response.data.message;
      }
    }

    showToast.error(errorMessage);
    throw error;
  }
};

// Helper function for handling delete success
function handleDeleteSuccess(response: any): boolean {
  // Check for various success response formats
  if (response.data?.status === "Request successful!" ||
    response.data?.status === "Deleted!" ||
    response.status === 200 ||
    response.status === 204) {
    return true;
  }

  throw new Error('Invalid response format from API');
}

/**
 * Delete a partner document
 * @param partnerIdentifier Partner ID or mobile number
 * @param documentType Document type to delete (shopFront, shopInner, evCer)
 * @param token Authentication token
 * @param isUsingMobile Flag to indicate if using mobile number instead of ID
 * @returns Promise indicating success
 */
export const deletePartnerDocument = async (
  partnerIdentifier: string,
  documentType: string,
  token: string | null,
  isUsingMobile: boolean = false
): Promise<boolean> => {
  try {
    console.log(`[API] Deleting document ${documentType} for partner ${isUsingMobile ? 'mobile' : 'ID'}: ${partnerIdentifier}`);

    if (!token) {
      console.error('[API] Missing authentication token for document deletion');
      showToast.error('Authentication required to delete document');
      return false;
    }

    if (!partnerIdentifier || !documentType) {
      console.error('[API] Missing required parameters for document deletion');
      showToast.error('Missing required information for document deletion');
      return false;
    }

    try {
      let endpoint = '';

      if (isUsingMobile) {
        // API path for deletion by mobile number
        endpoint = `/adminusers/partner/docs/${documentType}/${partnerIdentifier}`;
      } else {
        // API path for deletion by partner ID
        endpoint = `/adminusers/partner/docs/${documentType}/${partnerIdentifier}`;
      }

      console.log(`[API] Using endpoint: ${endpoint}`);

      const response = await axios.delete(endpoint, {
        headers: {
          'Content-Type': 'application/json',
          'token': token
        }
      });

      console.log('[API] Delete document response:', response.data);

      if (response.data?.status === "Request successful!") {
        // After successfully deleting the document, check if all required documents are now deleted
        // If all are deleted, then update the partner's KYC docs flag to false
        try {
          // Define which documents are required (shopFront and shopInner are required, evCer is optional)
          const requiredDocTypes = ['shopFront', 'shopInner'];
          let requiredDocsDeleted = true;

          // Check if the required document types still exist
          for (const type of requiredDocTypes) {
            // Skip checking the document we just deleted
            if (type === documentType) continue;

            try {
              const checkEndpoint = `/adminusers/partner/docs/${type}/${partnerIdentifier}`;
              const checkResponse = await axios.get(checkEndpoint, {
                headers: {
                  'Content-Type': 'application/json',
                  'token': token
                }
              });

              // If we successfully get a URL, at least one required document still exists
              if (checkResponse.data?.status === "Request successful!" &&
                checkResponse.data.result?.url) {
                console.log(`[API] Required document still exists: ${type}`);
                requiredDocsDeleted = false;
                break;
              }
            } catch (checkError: any) {
              // If the error is a 404 Not Found, it means the document doesn't exist
              if (checkError.response?.status === 404) {
                console.log(`[API] Required document doesn't exist: ${type}`);
              } else {
                // For other errors, we assume the document might still exist
                console.error(`[API] Error checking document ${type}:`, checkError);
                requiredDocsDeleted = false;
                break;
              }
            }
          }

          // Only update the kyc.docs flag if all required documents are deleted
          if (requiredDocsDeleted) {
            console.log('[API] All required documents have been deleted, updating kyc.docs flag to false');

            // Determine which endpoint to use for the update
            const updateEndpoint = isUsingMobile
              ? `/adminusers/partner?mobileNo=${partnerIdentifier}`
              : `/adminusers/partner?id=${partnerIdentifier}`;

            // The update payload needs to set kyc.docs to false
            const updatePayload = {
              kyc: {
                docs: false
              }
            };

            console.log(`[API] Updating partner KYC docs flag at endpoint: ${updateEndpoint}`);

            // Call the update endpoint
            const updateResponse = await axios.put(updateEndpoint, updatePayload, {
              headers: {
                'Content-Type': 'application/json',
                'token': token
              }
            });

            console.log('[API] Partner KYC flag update response:', updateResponse.data);
          } else {
            console.log('[API] Some required documents still exist, keeping kyc.docs flag as true');
          }
        } catch (updateError) {
          console.error('[API] Error checking or updating partner KYC docs flag:', updateError);
          // We don't fail the overall operation if just the flag update fails
        }

        return true;
      } else {
        console.warn('[API] Delete document returned success=false:', response.data);
        return false;
      }
    } catch (error: any) {
      console.error('[API] Error deleting document:', error.response?.data || error.message);
      const errorMessage = error.response?.data?.message || 'Failed to delete document';
      showToast.error(errorMessage);
      return false;
    }
  } catch (error: any) {
    console.error('[API] Error in deletePartnerDocument function:', error);
    const errorMessage = error.response?.data?.message || error.message || 'Failed to delete document';
    showToast.error(errorMessage);

    return false;
  }
};

/**
 * Fetch a partner document URL by mobile number or ID
 * @param partnerIdentifier Partner ID or mobile number
 * @param documentType Document type to fetch (shopFront, shopInner, evCer)
 * @param token Authentication token
 * @param isUsingMobile Flag to indicate if using mobile number instead of ID
 * @returns Promise with document URL
 */
export const fetchPartnerDocument = async (
  partnerIdentifier: string,
  documentType: string,
  token: string | null,
  isUsingMobile: boolean = false
): Promise<string | null> => {
  try {
    console.log(`[API DEBUG] Fetching document ${documentType} for partner ${isUsingMobile ? 'mobile' : 'ID'}: ${partnerIdentifier}`);
    console.log(`[API DEBUG] Token available: ${!!token}`);

    if (!token) {
      console.error('[API DEBUG] Missing authentication token for document fetch');
      showToast.error('Authentication required to view document');
      return null;
    }

    if (!partnerIdentifier || !documentType) {
      console.error('[API DEBUG] Missing required parameters for document fetch');
      showToast.error('Missing required information for document fetch');
      return null;
    }

    let endpoint = '';

    if (isUsingMobile) {
      // API path for fetching by mobile number
      endpoint = `/adminusers/partner/docs/${documentType}/${partnerIdentifier}`;
    } else {
      // API path for fetching by partner ID
      endpoint = `/adminusers/partner/docs/${documentType}/${partnerIdentifier}`;
    }

    console.log(`[API DEBUG] Using endpoint: ${endpoint}`);

    try {
      console.log(`[API DEBUG] Sending GET request to: ${endpoint}`);
      const response = await axios.get(endpoint, {
        headers: {
          'Content-Type': 'application/json',
          'token': token
        }
      });

      console.log('[API DEBUG] Fetch document response status:', response.status);
      console.log('[API DEBUG] Fetch document response data:', response.data);

      if (response.data?.status === "Request successful!" && response.data.result?.url) {
        console.log(`[API DEBUG] Successfully got document URL: ${response.data.result.url.substring(0, 60)}...`);
        return response.data.result.url;
      } else {
        console.warn('[API DEBUG] Fetch document returned without URL:', response.data);
        return null;
      }
    } catch (error: any) {
      console.error('[API DEBUG] Error in axios request:', error.message);
      if (error.response) {
        console.error('[API DEBUG] Error response status:', error.response.status);
        console.error('[API DEBUG] Error response data:', error.response.data);
      }
      const errorMessage = error.response?.data?.message || 'Failed to fetch document';
      showToast.error(errorMessage);
      return null;
    }
  } catch (error: any) {
    console.error('[API DEBUG] Error fetching document:', error);
    const errorMessage = error.response?.data?.message || 'Failed to fetch document';
    showToast.error(errorMessage);
    return null;
  }
};